import { SOCCER_AIM_MAX_RAD, SOCCER_AIM_STEP_RAD, SPORTS_CHARGE_MS, type TennisAim } from "@townsy/contracts";
import { useSyncExternalStore } from "react";
import { create } from "zustand";

/**
 * Townsy Sports Center, client only (ADR-0016 round 2): what the overlay and the 3D share while a visitor lines up a
 * shot. The overlay writes the charge (hold F / the big button) and the soccer aim nudges; the 3D writes what it can
 * see from the visitor's own avatar (distance to the hoop, the tennis aim zone, whether the tennis ball is in reach).
 * Nothing here decides anything: the released power and aim go to the server, which decides.
 */
export type ChargeKind = "shoot" | "kick";
/** Tennis timing cue for the visitor: no ball coming, the ball is coming but not in reach yet, or swing now. */
export type SwingCue = "none" | "coming" | "ready";

export interface SportsAimState {
  charging: { kind: ChargeKind; startedAt: number } | null;
  /** Soccer: aim offset from the way the visitor faces (radians, A / D or the arrow buttons). */
  aimOffset: number;
  /** Basketball: horizontal distance from the visitor to the nearest hoop (0.1 m steps), null off the court. */
  hoopDistance: number | null;
  /** Tennis: the part of the opponent's court a swing now would aim at (from the visitor's facing). */
  tennisAim: TennisAim;
  swingCue: SwingCue;
  /** The visitor's own facing (from their drawn avatar), for the kick's aim; null before the 3D saw it. */
  facing: number | null;
  startCharge(kind: ChargeKind, now: number): void;
  /** End the charge; returns its power (0..1), or null when nothing was charging. */
  endCharge(now: number): { kind: ChargeKind; power: number } | null;
  cancelCharge(): void;
  nudgeAim(dir: -1 | 1): void;
  resetAim(): void;
  setSeen(patch: Partial<Pick<SportsAimState, "hoopDistance" | "tennisAim" | "swingCue" | "facing">>): void;
}

/** HARNESS ONLY: a fixed power for photos of a charge in progress. Never set by the app. */
export const CHARGE_PHOTO: { power: number | null } = { power: null };

/** Power of a charge started at `startedAt` (fills in SPORTS_CHARGE_MS, then stays full). */
export function chargePower(startedAt: number, now: number): number {
  if (CHARGE_PHOTO.power !== null) return CHARGE_PHOTO.power;
  return Math.min(1, Math.max(0, (now - startedAt) / SPORTS_CHARGE_MS));
}

export const useSportsAim = create<SportsAimState>()((set, get) => ({
  charging: null,
  aimOffset: 0,
  hoopDistance: null,
  tennisAim: 0,
  swingCue: "none",
  facing: null,
  startCharge: (kind, now) => {
    if (get().charging) return;
    set({ charging: { kind, startedAt: now } });
  },
  endCharge: (now) => {
    const c = get().charging;
    if (!c) return null;
    set({ charging: null });
    return { kind: c.kind, power: chargePower(c.startedAt, now) };
  },
  cancelCharge: () => set({ charging: null }),
  nudgeAim: (dir) => set((s) => ({ aimOffset: Math.max(-SOCCER_AIM_MAX_RAD, Math.min(SOCCER_AIM_MAX_RAD, Math.round((s.aimOffset + dir * SOCCER_AIM_STEP_RAD) * 1000) / 1000)) })),
  resetAim: () => set({ aimOffset: 0 }),
  setSeen: (patch) => {
    const s = get();
    if (
      (patch.hoopDistance === undefined || patch.hoopDistance === s.hoopDistance) &&
      (patch.tennisAim === undefined || patch.tennisAim === s.tennisAim) &&
      (patch.swingCue === undefined || patch.swingCue === s.swingCue) &&
      (patch.facing === undefined || patch.facing === s.facing)
    ) {
      return;
    }
    set(patch);
  },
}));

/**
 * Read a slice of the aim store in a component. The server snapshot is the current state (not the initial one), so a
 * server-side render shows what the store holds; the selector must return a primitive or a stable reference.
 */
export function useAim<T>(selector: (s: SportsAimState) => T): T {
  const get = () => selector(useSportsAim.getState());
  return useSyncExternalStore(useSportsAim.subscribe, get, get);
}
